import { FunctionComponent, useState, useCallback, MouseEvent } from 'react';
import styled from 'styled-components';
import LevelDialog from './LevelDialog/index';
import CardTitle from './CardTitle';
import CardDescription from './CardDescription';
import TacticCounter from './TacticCounter';
import ActionButton from './ActionButton';

export type CardType = {
  className?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  onButtonClick?: () => void;
  tactics?: Array<{ id: string; description: string }>;
  showTacticCount?: boolean;
  showButton?: boolean;
  onClick?: () => void;
  titleMaxLength?: number;
  characterLimit?: number;
  'data-cy'?: string;
  showMoreDataCy?: string;
  buttonVariant?: 'primary' | 'default';
  buttonDisabled?: boolean;
};

const CardRoot = styled.div`
  width: 448px;
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid #dedede;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0px;
  line-height: normal;
  letter-spacing: normal;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const CardBody = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 16px 15px;
  gap: 12px;
  text-align: left;
  font-size: 16px;
  color: #1b1a1c;
  font-family: Lato, sans-serif;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Card: FunctionComponent<CardType> = ({
  className = '',
  title = 'Level',
  description = 'Local leaders have a comprehensive overview of the quality of services available throughout the community and the needs that are not being met.',
  buttonText = 'See Tactics',
  onButtonClick,
  tactics = [],
  showTacticCount = true,
  showButton = true,
  onClick,
  titleMaxLength,
  characterLimit: customCharacterLimit,
  'data-cy': dataCy,
  showMoreDataCy,
  buttonVariant = 'primary',
  buttonDisabled = false,
}) => {
  const [isAddDetailsOpen, setAddDetailsOpen] = useState(false);

  const openAddDetails = useCallback(() => {
    setAddDetailsOpen(true);
  }, []);

  const closeAddDetails = useCallback(() => {
    setAddDetailsOpen(false);
  }, []);

  // Use a larger character limit when the button is hidden, or use custom limit if provided
  const characterLimit = customCharacterLimit || (showButton ? 120 : 240);

  const handleActionButtonClick = useCallback(
    (e?: MouseEvent<HTMLDivElement>) => {
      if (e) {
        e.stopPropagation();
      }
      if (onButtonClick) {
        onButtonClick();
      } else {
        openAddDetails();
      }
    },
    [onButtonClick, openAddDetails],
  );

  const handleCardClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  const handleDescriptionClick = useCallback(
    (e?: MouseEvent<HTMLDivElement>) => {
      if (e) {
        e.stopPropagation();
      }
      openAddDetails();
    },
    [openAddDetails],
  );
  const levelId = dataCy ? dataCy.match(/level(\d+)_description/)?.[1] : '';
  const buttonDataCy = levelId ? `level${levelId}_tactic_details_btn` : '';
  const tacticDataCy = levelId ? `level${levelId}_total_tactics` : '';

  return (
    <>
      <CardRoot
        className={className}
        onClick={handleCardClick}
        data-cy={dataCy}
      >
        <CardBody>
          <CardTitle title={title} maxLength={titleMaxLength} />

          <CardDescription
            description={description}
            onClick={handleDescriptionClick}
            characterLimit={characterLimit}
            data-cy={dataCy}
            showMoreDataCy={showMoreDataCy}
          />

          {showTacticCount && (
            <TacticCounter count={tactics.length} data-cy={tacticDataCy} />
          )}

          {showButton && buttonText && (
            <ActionButton
              text={buttonText}
              onClick={handleActionButtonClick}
              data-cy={buttonDataCy}
              variant={buttonVariant}
              disabled={buttonDisabled}
            />
          )}
        </CardBody>
      </CardRoot>

      {isAddDetailsOpen && (
        <LevelDialog
          overlayColor="rgba(113, 113, 113, 0.3)"
          onOutsideClick={closeAddDetails}
          tacticTitle={title}
          tacticDescription={description}
        />
      )}
    </>
  );
};

export default Card;
